import { startAuthentication } from '@simplewebauthn/browser';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useSession } from '../session';

export function Mfa() {
  const { me, refresh } = useSession();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { returnTo?: string } };
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const returnTo = location.state?.returnTo ?? '/';

  useEffect(() => {
    if (me?.mfaVerified) navigate(returnTo, { replace: true });
  }, [me, navigate, returnTo]);

  async function done() {
    await refresh();
    navigate(returnTo, { replace: true });
  }

  async function usePasskey() {
    setBusy(true);
    setError(null);
    try {
      const optionsJSON = await api<Parameters<typeof startAuthentication>[0]['optionsJSON']>('/api/auth/passkeys/login/options', { method: 'POST' });
      const response = await startAuthentication({ optionsJSON });
      await api('/api/auth/passkeys/login/verify', { method: 'POST', body: { response } });
      await done();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The passkey step did not complete');
    } finally {
      setBusy(false);
    }
  }

  async function useCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/totp/verify', { method: 'POST', body: { code } });
      await done();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The code was not accepted');
    } finally {
      setBusy(false);
    }
  }

  if (!me) return null;
  const canPasskey = me.factors.passkeys.length > 0;

  return (
    <div className="card narrow">
      <h1>Second step</h1>
      <p className="lede">Signed in as {me.user.email}. Confirm it is you.</p>
      {error && <div className="alert">{error}</div>}
      {/* A passkey is the stronger of the two and the quicker, so where there is one it is the primary
          button and the code form below it is the fallback, styled as such. */}
      {canPasskey && (
        <button type="button" className="block" style={{ marginBottom: 20 }} onClick={usePasskey} disabled={busy}>
          Use a passkey
        </button>
      )}
      {me.factors.totp && (
        <form onSubmit={useCode}>
          <label>
            {canPasskey ? 'Or enter the code from your authenticator app' : 'Enter the code from your authenticator app'}
            <input id="totp-code" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          </label>
          <button type="submit" className={canPasskey ? 'secondary block' : 'block'} disabled={busy || code.length !== 6}>Verify code</button>
        </form>
      )}
      <p className="small muted" style={{ marginTop: 20, marginBottom: 0 }}>
        No access to any of these? <Link to="/forgot-factors">Ask an owner to verify you</Link>.
      </p>
    </div>
  );
}
